import React from "react";

import styles from "./Button.module.scss";
import { sizeClassMap, variantClassMap } from "./Button.constants";
import type { ButtonProps } from "./Button.types";
import { cn } from "../../utils/cn";

export function Button<C extends React.ElementType = "button">({
  variant = "primary",
  size = "md",
  iconLeft,
  iconRight,
  fullWidth,
  className,
  children,
  as,
  isDisabled,
  ...rest
}: ButtonProps<C>) {
  const classes = cn(
    styles.Button,
    styles[variantClassMap[variant]],
    styles[sizeClassMap[size]],
    fullWidth ? styles.FullWidth : null,
    className,
  );

  const content = (
    <>
      {iconLeft && (
        <span className={styles.Icon}>{iconLeft}</span>
      )}
      <span className={styles.Label}>{children}</span>
      {iconRight && (
        <span className={styles.Icon}>{iconRight}</span>
      )}
    </>
  );

  const shouldRenderAnchor =
    as === "a" ||
    (!as && "href" in rest && typeof rest.href === "string");

  if (shouldRenderAnchor) {
    const { onClick, ...anchorProps } =
      rest as React.AnchorHTMLAttributes<HTMLAnchorElement>;
    return (
      <a
        {...anchorProps}
        role="button"
        aria-disabled={isDisabled || undefined}
        tabIndex={isDisabled ? -1 : anchorProps.tabIndex}
        className={classes}
        onClick={(event) => {
          if (isDisabled) {
            event.preventDefault();
            event.stopPropagation();
            return;
          }
          onClick?.(event);
        }}
      >
        {content}
      </a>
    );
  }

  if (as && as !== "button") {
    const Component = as as React.ElementType;

    if (isDisabled) {
      return (
        <Component
          {...(rest as React.ComponentPropsWithoutRef<React.ElementType>)}
          aria-disabled
          tabIndex={-1}
          className={classes}
          onClick={(event: React.MouseEvent<HTMLElement>) => {
            event.preventDefault();
            event.stopPropagation();
          }}
        >
          {content}
        </Component>
      );
    }

    return (
      <Component
        {...(rest as React.ComponentPropsWithoutRef<React.ElementType>)}
        className={classes}
      >
        {content}
      </Component>
    );
  }

  const { type = "button", ...buttonProps } =
    rest as React.ButtonHTMLAttributes<HTMLButtonElement>;

  return (
    <button
      {...buttonProps}
      type={type}
      disabled={isDisabled}
      className={classes}
    >
      {content}
    </button>
  );
}
